/**
 * SignalR Vue Plugin
 * Provides global access to SignalR service throughout the Vue application
 * Enhanced with connection resilience monitoring
 */

import signalRService from '../services/signalRService.js'

const SignalRPlugin = {
  install(app, options = {}) {
    // Add SignalR service to global properties
    app.config.globalProperties.$signalR = signalRService

    // Add SignalR service to provide/inject
    app.provide('signalR', signalRService)

    // Add reactive connection status
    app.config.globalProperties.$signalRConnected = false

    // Listen to connection events
    signalRService.on('connected', () => {
      app.config.globalProperties.$signalRConnected = true
      console.log('SignalR: Vue app notified of connection')
    })

    signalRService.on('disconnected', () => {
      app.config.globalProperties.$signalRConnected = false
      console.log('SignalR: Vue app notified of disconnection')
    })

    signalRService.on('reconnected', () => {
      app.config.globalProperties.$signalRConnected = true
      console.log('SignalR: Vue app notified of reconnection')
    })

    // Add global methods
    app.config.globalProperties.$connectSignalR = async () => {
      try {
        await signalRService.connect()
        return true
      } catch (error) {
        console.error('SignalR: Failed to connect:', error)
        return false
      }
    }

    app.config.globalProperties.$disconnectSignalR = async () => {
      try {
        await signalRService.disconnect()
        return true
      } catch (error) {
        console.error('SignalR: Failed to disconnect:', error)
        return false
      }
    }

    app.config.globalProperties.$sendSignalRMessage = async (message) => {
      try {
        await signalRService.sendMessage(message)
        return true
      } catch (error) {
        console.error('SignalR: Failed to send message:', error)
        return false
      }
    }

    app.config.globalProperties.$reconnectSignalR = async () => {
      try {
        await signalRService.reconnect()
        return true
      } catch (error) {
        console.error('SignalR: Failed to reconnect:', error)
        return false
      }
    }

    // Add global computed properties
    app.config.globalProperties.$isSignalRConnected = () => {
      return signalRService.getConnected()
    }

    app.config.globalProperties.$getSignalRConnectionState = () => {
      return signalRService.getConnectionState()
    }

    app.config.globalProperties.$getSignalRConnectionHealth = () => {
      return signalRService.getConnectionResilienceStatus()
    }

    app.config.globalProperties.$isSignalRConnectionHealthy = () => {
      const status = signalRService.getConnectionResilienceStatus()
      return status.isConnected && status.isOnline
    }

    app.config.globalProperties.$isSignalRPageVisible = () => {
      const status = signalRService.getConnectionResilienceStatus()
      return status.isPageVisible
    }

    app.config.globalProperties.$isSignalROnline = () => {
      const status = signalRService.getConnectionResilienceStatus()
      return status.isOnline
    }

    // Add global event listeners
    app.config.globalProperties.$onSignalREvent = (event, callback) => {
      signalRService.on(event, callback)
    }

    app.config.globalProperties.$offSignalREvent = (event, callback) => {
      signalRService.off(event, callback)
    }

    // Add connection resilience methods
    app.config.globalProperties.$setSignalRAutoReconnect = (enabled) => {
      signalRService.setAutoReconnect(enabled)
    }

    app.config.globalProperties.$getSignalRResilienceStatus = () => {
      return signalRService.getConnectionResilienceStatus()
    }

    // Auto-connect on app initialization (if enabled)
    if (options.autoConnect !== false) {
      // Wait for app to be mounted before connecting
      app.mixin({
        async mounted() {
          if (this.$options.name === 'App') {
            // Only connect from the root App component
            setTimeout(async () => {
              try {
                await signalRService.connect()
              } catch (error) {
                console.warn('SignalR: Auto-connect failed:', error)
              }
            }, 1000) // Delay to ensure app is fully loaded
          }
        }
      })
    }

    // Cleanup on app unmount
    app.mixin({
      async beforeUnmount() {
        if (this.$options.name === 'App') {
          try {
            signalRService.cleanup()
          } catch (error) {
            console.warn('SignalR: Cleanup failed:', error)
          }
        }
      }
    })

    // Add page visibility and network status monitoring
    app.mixin({
      mounted() {
        // Monitor page visibility changes
        const handleVisibilityChange = () => {
          const isVisible = !document.hidden
          console.log('SignalR: Page visibility changed in Vue app:', isVisible)
          
          // Emit global event for components to listen to
          this.$emit('signalr-visibility-change', isVisible)
        }

        // Monitor network status changes
        const handleOnlineStatus = () => {
          const isOnline = navigator.onLine
          console.log('SignalR: Network status changed in Vue app:', isOnline)
          
          // Emit global event for components to listen to
          this.$emit('signalr-network-change', isOnline)
        }

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('online', handleOnlineStatus)
        window.addEventListener('offline', handleOnlineStatus)

        // Store cleanup function
        this._signalRCleanup = () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange)
          window.removeEventListener('online', handleOnlineStatus)
          window.removeEventListener('offline', handleOnlineStatus)
        }
      },

      beforeUnmount() {
        // Cleanup event listeners
        if (this._signalRCleanup) {
          this._signalRCleanup()
        }
      }
    })

    // Add global error handling for SignalR
    app.config.errorHandler = (error, instance, info) => {
      // Check if error is related to SignalR
      if (error.message && error.message.includes('SignalR')) {
        console.error('SignalR Error:', error)
        
        // Emit global error event
        app.config.globalProperties.$emit('signalr-error', error)
        
        // Try to reconnect if connection is lost
        if (!signalRService.getConnected()) {
          setTimeout(async () => {
            try {
              await signalRService.reconnect()
            } catch (reconnectError) {
              console.error('SignalR: Reconnection failed after error:', reconnectError)
            }
          }, 2000)
        }
      }
    }
  }
}

export default SignalRPlugin 